import { Button, Callout, Field, Input, Textarea } from '@wtfalch/design';
import {
  clearVacationResponse,
  type MailClient,
  setVacationResponse,
  type VacationResponse,
  vacationResponse,
} from '@wtfalch/email/mailbox';
import { useEffect, useState } from 'react';

import { dayToIso, isoToDay } from './awayDates.ts';

/**
 * The away reply (`Mail-away` board): a subject, a message and a from-to
 * window, with one button that turns it on and one that turns it off. Turning
 * it off keeps the text, so turning it back on needs nothing typed again.
 */
export function Away({
  client,
  onChange,
}: {
  client: MailClient;
  /** Reports whether the reply is running, for the banner in Mail. */
  onChange?: (response: VacationResponse) => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    let live = true;
    vacationResponse(client)
      .then((v) => {
        if (!live) return;
        setEnabled(v.isEnabled);
        setSubject(v.subject ?? '');
        setMessage(v.textBody ?? '');
        setFrom(isoToDay(v.fromDate));
        setTo(isoToDay(v.toDate));
        setLoaded(true);
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      live = false;
    };
  }, [client]);

  const todayDay = isoToDay(new Date().toISOString());
  const problem = !message.trim()
    ? 'Write the message people get.'
    : from && to && to < from
      ? 'The last day is before the first day.'
      : to && to < todayDay
        ? 'The last day has already passed.'
        : '';

  const run = async (turnOn: boolean) => {
    setError('');
    setSaved('');
    if (turnOn && problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    try {
      if (turnOn) {
        await setVacationResponse(client, {
          isEnabled: true,
          subject: subject.trim() || null,
          textBody: message,
          fromDate: from ? dayToIso(from, false) : null,
          toDate: to ? dayToIso(to, true) : null,
        });
      } else {
        await clearVacationResponse(client);
      }
      const now = await vacationResponse(client);
      setEnabled(now.isEnabled);
      onChange?.(now);
      setSaved(turnOn ? 'Your away reply is on.' : 'Your away reply is off.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 mail-set-form">
      <p className="text-sm text-muted m-0">
        Anyone who writes to you while this is on gets your message back, once.
      </p>
      <Field label="Subject" hint="Left empty, the server picks one.">
        <Input
          block
          value={subject}
          disabled={!loaded}
          onChange={(e) => setSubject(e.target.value)}
        />
      </Field>
      <Field label="Message">
        <Textarea
          rows={6}
          value={message}
          disabled={!loaded}
          onChange={(e) => setMessage(e.target.value)}
        />
      </Field>
      <div className="flex gap-4 flex-wrap">
        <Field label="From" hint="Empty: right away.">
          <Input
            type="date"
            value={from}
            disabled={!loaded}
            onChange={(e) => setFrom(e.target.value)}
          />
        </Field>
        <Field label="Until" hint="Empty: until you turn it off.">
          <Input
            type="date"
            value={to}
            disabled={!loaded}
            onChange={(e) => setTo(e.target.value)}
          />
        </Field>
      </div>
      <div className="mail-set-status" role="status">
        {error ? (
          <Callout tone="bad">{error}</Callout>
        ) : saved ? (
          <Callout tone="good">{saved}</Callout>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        <Button kind="primary" disabled={!loaded || busy} onPress={() => void run(true)}>
          {enabled ? 'Save changes' : 'Turn on'}
        </Button>
        {enabled && (
          <Button disabled={busy} onPress={() => void run(false)}>
            Turn off
          </Button>
        )}
      </div>
    </div>
  );
}
